import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy, OnInit, signal, viewChild } from '@angular/core';
import { assetIdSignalFromRoute } from '../../../../../utils/asset-route.utils';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { MenuItem } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { CesPopupMenuComponent } from '../../../../ui/ces-popup-menu/ces-popup-menu.component';
import { TableLazyLoadEvent } from 'primeng/table';
import { filter, Subject, takeUntil } from 'rxjs';
import { HasPermissionDirective } from '../../../../../directives/has-permission.directive';
import { tableDefinition } from '../../../../../entity-definitions/table.definition';
import { TableApiService } from '../../../../../services/api/table.service';
import { PaginatedUrlSyncService, ParsedFilters } from '../../../../../services/paginated-url-sync.service';
import { ConfirmationHelperService } from '../../../../../services/confirmation-helper.service';
import { ModalHelperService } from '../../../../../services/modal-helper.service';
import { ToastService } from '../../../../../services/toast.service';
import { TableStore } from '../../../../../stores/table.store';
import { EntityDefinition } from '../../../../../types/entity-definition.interface';
import { MenuItemWithIcon } from '../../../../../types/menu.interface';
import { PermissionScope } from '../../../../../types/permission-scope.enum';
import { assetWriteSignal } from '../../../../../utils/asset-permissions.utils';
import { buildEntityMenuItems } from '../../../../../utils/entity-menu.utils';
import { CesTable } from '../../../../../types/table.interface';
import { LazyLoadConverter } from '../../../../../utils/lazy-load.converter';
import { GenericParentGridComponent } from '../../../../generic/generic-parent-grid.component';
import { CesAssetBreadcrumbComponent } from '../../../../ui/ces-asset-breadcrumb/ces-asset-breadcrumb.component';
import { CesButtonComponent } from '../../../../ui/ces-buttons/ces-button.component';
import { CesTitleComponent } from '../../../../ui/ces-route-title/ces-route-title.component';
import { CreateTableDialogComponent } from './create-table-dialog.component';

@Component({
  selector: 'ces-tables-list',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DialogService],
  imports: [
    CesAssetBreadcrumbComponent,
    CesButtonComponent,
    CesTitleComponent,
    GenericParentGridComponent,
    CesPopupMenuComponent,
    HasPermissionDirective,
    TranslocoModule,
  ],
  templateUrl: './tables-list.component.html',
})
export class CesTablesListComponent implements OnInit, OnDestroy {
  protected readonly PermissionScope = PermissionScope;

  readonly #destroy$ = new Subject<void>();
  #isLoadingFromGrid = false;
  #isInitialLoad = true;
  #dialogRef: DynamicDialogRef | null = null;

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly tableStore = inject(TableStore);
  private readonly tableApiService = inject(TableApiService);
  private readonly confirmationHelper = inject(ConfirmationHelperService);
  private readonly modalHelper = inject(ModalHelperService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslocoService);
  private readonly dialogService = inject(DialogService);
  private readonly paginatedUrlSync = inject(PaginatedUrlSyncService);

  readonly actionMenu = viewChild.required<CesPopupMenuComponent>('actionMenu');
  readonly selectedTable = signal<CesTable | null>(null);
  readonly isDeleting = signal(false);

  readonly currentAssetId = assetIdSignalFromRoute(this.route);
  private readonly canWriteAsset = assetWriteSignal(PermissionScope.Tables, this.currentAssetId);

  readonly entityDefinition = computed((): EntityDefinition<CesTable> => {
    const assetId = this.currentAssetId() ?? undefined;
    const customActions = tableDefinition.actionButtons?.parent?.custom?.map(action =>
      action.hasPermission ? { ...action, hasPermission: { ...action.hasPermission, assetId } } : action,
    );
    return {
      ...tableDefinition,
      actionButtons: {
        ...tableDefinition.actionButtons,
        parent: {
          ...tableDefinition.actionButtons?.parent,
          ...(customActions ? { custom: customActions } : {}),
        },
      },
      rowLinkHref: table => (table.assetId ? ['/assets', table.assetId, 'analysis', 'tables', table.id] : null),
    };
  });
  readonly urlFilters = signal<ParsedFilters | undefined>(
    this.paginatedUrlSync.parseFiltersFromUrl(this.route.snapshot.queryParams, this.entityDefinition()),
  );

  readonly tables = computed(() => {
    const assetId = this.currentAssetId();
    return assetId ? this.tableStore.tablesByAssetId(assetId) : [];
  });

  readonly totalRecords = computed(() => this.tableStore.paginationState().totalItems);
  readonly first = computed(() => {
    const page = this.tableStore.paginationState().currentPage;
    const limit = this.tableStore.paginationState().limit;
    return (page - 1) * limit;
  });
  readonly rows = computed(() => this.tableStore.paginationState().limit);

  ngOnInit(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        filter(event => event.url.includes('/analysis/tables') && !event.url.match(/\/analysis\/tables\/\d+$/)),
        takeUntil(this.#destroy$),
      )
      .subscribe(() => {
        if (this.#isLoadingFromGrid) return;
        const assetId = this.currentAssetId();
        if (assetId) {
          void this.#loadTablesFromUrl(assetId);
        }
      });

    const assetId = this.currentAssetId();
    if (assetId) {
      void this.#loadTablesFromUrl(assetId);
    }
  }

  async #loadTablesFromUrl(assetId: number): Promise<void> {
    const { queryParams, toSync } = this.paginatedUrlSync.resolveState(
      this.route,
      this.paginatedUrlSync.buildStateKey('tables-grid', assetId),
    );
    const tableEvent = this.paginatedUrlSync.buildTableEventFromUrl(queryParams, this.entityDefinition());
    this.urlFilters.set(tableEvent.filters);

    if (toSync) {
      this.#isLoadingFromGrid = true;
      this.paginatedUrlSync.syncUrlInBackground(this.router, this.route, toSync, () => (this.#isLoadingFromGrid = false));
    }

    const params = LazyLoadConverter.fromTableEvent(tableEvent, ['name', 'description', 'measures']);
    await this.tableStore.loadPaginatedByAssetId(assetId, params);
  }

  readonly breadcrumbTrail = computed((): MenuItem[] => [{ label: this.translateService.translate('tables.breadcrumb') }]);

  readonly menuItems = computed((): MenuItemWithIcon[] =>
    buildEntityMenuItems(
      this.translateService,
      'tables.actions',
      {
        onEdit: () => this.onEditTable(),
        onDuplicate: () => this.onDuplicateTable(),
        onDelete: () => {
          void this.onDeleteTable();
        },
      },
      { canWrite: this.canWriteAsset() },
    ),
  );

  onRowAction(action: string, table: CesTable, event?: MouseEvent): void {
    if (action === 'menu' && event) {
      this.selectedTable.set(table);
      this.actionMenu().toggle(event);
    }
  }

  async onLazyLoad(event: TableLazyLoadEvent): Promise<void> {
    const assetId = this.currentAssetId();
    if (!assetId) return;

    if (this.#isInitialLoad) {
      this.#isInitialLoad = false;
      return;
    }

    this.#isLoadingFromGrid = true;

    try {
      const queryParams = this.paginatedUrlSync.buildQueryParamsFromEvent(event, this.entityDefinition());
      this.paginatedUrlSync.saveState(this.paginatedUrlSync.buildStateKey('tables-grid', assetId), queryParams);
      await this.paginatedUrlSync.navigateWithParams(this.router, this.route, queryParams);

      const params = LazyLoadConverter.fromTableEvent(event, ['name', 'description', 'measures']);
      await this.tableStore.loadPaginatedByAssetId(assetId, params);
    } finally {
      this.isDeleting.set(false);
      this.#isLoadingFromGrid = false;
    }
  }

  private onEditTable(): void {
    const table = this.selectedTable();
    if (!table || this.isDeleting()) return;

    const assetId = this.currentAssetId();
    if (!assetId) return;

    const existingTableNames = this.tables().map((t: CesTable) => t.name);
    this.#dialogRef = this.dialogService.open(
      CreateTableDialogComponent,
      this.modalHelper.getFormConfig({
        header: this.translateService.translate('tables.actions.edit'),
        data: { mode: 'edit', table, existingTableNames },
      }),
    );

    this.#dialogRef.onClose.subscribe((result: Partial<CesTable> | undefined) => {
      if (!result?.id) return;
      void this.applyEditTable(assetId, result as Partial<CesTable> & { id: number });
    });
  }

  private async applyEditTable(assetId: number, result: Partial<CesTable> & { id: number }): Promise<void> {
    try {
      await this.tableApiService.updateAsync(result.id, { ...result, assetId });
      this.tableStore.invalidateCache();
      void this.#loadTablesFromUrl(assetId);
    } catch {
      this.toastService.error(this.translateService.translate('common.toasts.operationFailed'));
    }
  }

  private onDuplicateTable(): void {
    const table = this.selectedTable();
    if (!table || this.isDeleting()) return;

    const assetId = this.currentAssetId();
    if (!assetId) return;

    const existingTableNames = this.tables().map((t: CesTable) => t.name);
    this.#dialogRef = this.dialogService.open(
      CreateTableDialogComponent,
      this.modalHelper.getFormConfig({
        header: this.translateService.translate('tables.actions.duplicate'),
        data: { mode: 'duplicate', table, existingTableNames },
      }),
    );

    this.#dialogRef.onClose.subscribe((result: Partial<CesTable> | undefined) => {
      if (!result) return;
      void this.applyDuplicateTable(table, assetId, result);
    });
  }

  private async applyDuplicateTable(table: CesTable, assetId: number, result: Partial<CesTable>): Promise<void> {
    try {
      const created = await this.tableApiService.createAsync({
        ...result,
        assetId,
        metadata: table.metadata,
      } as Omit<CesTable, 'id'>);
      await this.tableApiService.copyTableMeasuresAsync(table.id, created.id);
      this.tableStore.invalidateCache();
      void this.router.navigate(['/assets', assetId, 'analysis', 'tables', created.id]);
    } catch {
      this.toastService.error(this.translateService.translate('common.toasts.operationFailed'));
    }
  }

  private async onDeleteTable(): Promise<void> {
    const table = this.selectedTable();
    if (!table || this.isDeleting()) return;
    this.isDeleting.set(true);
    try {
      await this.confirmationHelper.confirmAndDelete({
        entityName: table.name,
        messageKey: 'tables.confirmDeleteMessage',
        onDelete: () => this.tableStore.delete(table.id),
        onSuccess: () => {
          this.tableStore.invalidateCache();
          const assetId = this.currentAssetId();
          if (assetId) void this.#loadTablesFromUrl(assetId);
        },
      });
    } finally {
      this.isDeleting.set(false);
    }
  }

  onCreateTable(): void {
    const assetId = this.currentAssetId();
    if (!assetId) return;

    const existingTableNames = this.tables().map((t: CesTable) => t.name);
    this.#dialogRef = this.dialogService.open(
      CreateTableDialogComponent,
      this.modalHelper.getFormConfig({
        header: this.translateService.translate('tables.createTable'),
        data: { mode: 'create', existingTableNames },
      }),
    );

    this.#dialogRef.onClose.subscribe((result: Partial<CesTable> | undefined) => {
      if (!result) return;
      void this.applyCreateTable(assetId, result);
    });
  }

  private async applyCreateTable(assetId: number, result: Partial<CesTable>): Promise<void> {
    try {
      const created = await this.tableApiService.createAsync({ ...result, assetId } as Omit<CesTable, 'id'>);
      this.tableStore.invalidateCache();
      void this.router.navigate(['/assets', assetId, 'analysis', 'tables', created.id, 'config']);
    } catch {
      this.toastService.error(this.translateService.translate('common.toasts.operationFailed'));
    }
  }

  ngOnDestroy(): void {
    this.#dialogRef?.close();
    this.#destroy$.next();
    this.#destroy$.complete();
  }
}
